import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface DMCARequest {
  id: string;
  reporterName: string;
  reporterEmail: string;
  reporterAddress: string;
  copyrightOwner: string;
  originalWorkDescription: string;
  infringingContentUrl: string;
  postId?: string;
  userId?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'COUNTER_CLAIMED';
  reviewNotes?: string;
  reviewedBy?: string;
  reviewedAt?: Date;
  counterClaimText?: string;
  counterClaimedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  post?: {
    id: string;
    title: string;
    user: {
      id: string;
      username: string;
      displayName: string;
    };
  };
  user?: {
    id: string;
    username: string;
    displayName: string;
    email: string;
  };
}

export interface DMCAStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  counterClaimed: number;
}

export interface SubmitDMCAData {
  reporterName: string;
  reporterEmail: string;
  reporterAddress: string;
  copyrightOwner: string;
  originalWorkDescription: string;
  infringingContentUrl: string;
  postId?: string;
  userId?: string;
}

export interface ApproveDMCAData {
  reviewNotes?: string;
  takedownContent?: boolean;
}

export interface RejectDMCAData {
  reviewNotes: string;
}

export interface CounterClaimData {
  counterClaimText: string;
}

const dmcaService = {
  // Submit DMCA request
  submit: async (data: SubmitDMCAData): Promise<DMCARequest> => {
    const response = await axios.post(`${API_URL}/api/admin/dmca`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get all DMCA requests (admin)
  getAll: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ requests: DMCARequest[]; total: number; page: number; totalPages: number }> => {
    const response = await axios.get(`${API_URL}/api/admin/dmca`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get DMCA statistics
  getStats: async (): Promise<DMCAStats> => {
    const response = await axios.get(`${API_URL}/api/admin/dmca/stats`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get single DMCA request
  getById: async (id: string): Promise<DMCARequest> => {
    const response = await axios.get(`${API_URL}/api/admin/dmca/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Approve DMCA request
  approve: async (id: string, data: ApproveDMCAData): Promise<DMCARequest> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/dmca/${id}/approve`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Reject DMCA request
  reject: async (id: string, data: RejectDMCAData): Promise<DMCARequest> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/dmca/${id}/reject`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Submit counter claim
  counterClaim: async (id: string, data: CounterClaimData): Promise<DMCARequest> => {
    const response = await axios.post(
      `${API_URL}/api/admin/dmca/${id}/counter-claim`,
      data,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Resolve counter claim
  resolveCounterClaim: async (id: string, approved: boolean, notes?: string): Promise<DMCARequest> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/dmca/${id}/resolve-counter-claim`,
      { approved, notes },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },
};

export default dmcaService;
